// src/admin/hooks/useBorrowers.ts
// Borrower roster for the admin borrowers list. Joins credit_scores so the
// table can show score + tier without an N+1 fetch.
//
// Schema notes (differs from the sample in the implementation plan):
//   - profiles has `phone`, not `phone_number`.
//   - profiles has no `is_borrowing_frozen` column; "freeze" is a product
//     concept that is not yet backed by a column, so we don't expose it
//     here. The BorrowerDetail "Freeze" modal (Task 60) will use whatever
//     mechanism ends up in the schema at that point.
//   - credit_scores.user_id is an FK to public.profiles(id) (NOT auth uid),
//     so PostgREST can embed it under the `credit_scores` key.
//   - credit_scores columns are `current_score` / `current_tier`.
//
// Generated types have `Relationships: []`, so supabase-js cannot infer the
// shape of the embedded select. We declare BorrowerRow explicitly and cast
// through `unknown` — the same pattern used by useLoan.
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@admin/lib/supabase';
import type { Database } from '@/lib/database.types';

type KycStatus = Database['public']['Enums']['kyc_status'];
type CreditTier = Database['public']['Enums']['credit_tier'];

export interface BorrowerRow {
  id: string;
  user_id: string;
  display_name: string | null;
  full_name: string | null;
  phone: string;
  email: string | null;
  kyc_status: KycStatus;
  deleted_at: string | null;
  credit_score: { current_score: number; current_tier: CreditTier } | null;
}

// Escape the three characters PostgREST treats as operator syntax inside a
// `.or()` filter value: comma, parenthesis, period. Without this, a user
// who types "john.doe" or "(+26…)" breaks the query.
function escapeOrValue(s: string): string {
  return s.replace(/[(),]/g, ' ').trim();
}

export function useBorrowers(search: string) {
  return useQuery({
    queryKey: ['borrowers', search],
    queryFn: async (): Promise<BorrowerRow[]> => {
      let q = supabase
        .from('profiles')
        .select(
          'id, user_id, display_name, full_name, phone, email, kyc_status, deleted_at, credit_scores(current_score, current_tier)',
        )
        .is('deleted_at', null)
        .order('display_name', { ascending: true, nullsFirst: false })
        .limit(200);
      const term = escapeOrValue(search);
      if (term.length > 0) {
        q = q.or(
          `display_name.ilike.%${term}%,full_name.ilike.%${term}%,phone.ilike.%${term}%,email.ilike.%${term}%`,
        );
      }
      const { data, error } = await q;
      if (error) throw error;

      // Embedded select comes back as `credit_scores: T[] | T | null`.
      // Normalize to a single object or null for the table renderer.
      type Raw = Omit<BorrowerRow, 'credit_score'> & {
        credit_scores:
          | { current_score: number; current_tier: CreditTier }
          | { current_score: number; current_tier: CreditTier }[]
          | null;
      };
      const rows = (data ?? []) as unknown as Raw[];
      return rows.map((r): BorrowerRow => {
        const cs = Array.isArray(r.credit_scores) ? r.credit_scores[0] ?? null : r.credit_scores;
        const { credit_scores: _drop, ...rest } = r;
        return { ...rest, credit_score: cs };
      });
    },
  });
}
